import React, { useState, useEffect } from 'react';
import {
  Button,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Menu,
  Switch,
  Text,
  makeStyles,
  tokens,
  Input,
  Avatar,
  SearchBox,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import { llmService } from '../../../../llm/services/LLMEnhancementService';
import {
  SettingsRegular,
  MoreHorizontalRegular,
  SearchRegular,
  CodeRegular,
  HomeRegular,
} from '@fluentui/react-icons';
import powerpointIcon from '../../../../assets/shared/Images/PowerPoint.svg';
import wordIcon from '../../../../assets/shared/Images/Word.svg';
import excelIcon from '../../../../assets/shared/Images/Excel.svg';
import onenoteIcon from '../../../../assets/shared/Images/OneNote.svg';
import formsIcon from '../../forms/Size=32, Theme=Color.svg';
import LLMAuthDialog from '../copilot/LLMAuthDialog';
import AzureOpenAIConfigDialog, { isAzureOpenAIConfigured } from '../copilot/AzureOpenAIConfigDialog';
import { useStyleContext, StyleType } from './StyleContext';

interface OfficeHeaderProps {
  appName: 'Word' | 'PowerPoint' | 'OneNote' | 'Excel' | 'Custom' | string;
  documentTitle?: string;
  documentName?: string;
  isFixed?: boolean;
  backgroundColor?: string;
  showAppSwitcher?: boolean;
  showSearch?: boolean;
  showSearchBox?: boolean;
  showSettings?: boolean;
  showMore?: boolean;
  showAvatar?: boolean;
  showUndoRedo?: boolean;
  showQuickAccess?: boolean;
  hideAccountButton?: boolean;
  isModified?: boolean;
  customRightSection?: React.ReactNode;
  customLeftSection?: React.ReactNode;
  customAccountMenu?: React.ReactNode;
  customButtons?: Array<{
    text: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  }>;
  quickAccessItems?: Array<{
    icon: string;
    tooltip: string;
    onClick: () => void;
  }>;
  onAppSwitcherClick?: () => void;
  onSettingsClick?: () => void;
  onMoreClick?: () => void;
  onSave?: () => void;
  onRename?: (newName: string) => void;
  searchPlaceholder?: string;
  avatarName?: string;
  avatarImage?: string;
  // LLM props
  onLLMToggleChange?: (enabled: boolean, isAuthenticated: boolean) => void;
}

const useStyles = makeStyles({
  header: {
    height: '48px',
    backgroundColor: tokens.colorNeutralBackground3, // Match original headers
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0',
  },
  headerFixed: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    width: '100%',
    zIndex: 1001,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    order: 1,
  },
  centerSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '468px',
    padding: '8px',
    order: 2,
    '@media (max-width: 1024px)': {
      display: 'none',
    },
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    order: 3,
  },
  appSwitcher: {
    padding: '12px',
    minWidth: '48px',
    height: '48px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      '& svg': {
        color: tokens.colorNeutralForeground2Hover,
      },
    },
    '& svg': {
      color: tokens.colorNeutralForeground2,
      width: '24px',
      height: '24px',
    },
  },
  appSwitcherSelected: {
    backgroundColor: `${tokens.colorNeutralBackground3Selected} !important`,
    '&:hover': {
      backgroundColor: `${tokens.colorNeutralBackground3Selected} !important`,
    },
    '&:active': {
      backgroundColor: `${tokens.colorNeutralBackground3Selected} !important`,
    },
    '&:focus': {
      backgroundColor: `${tokens.colorNeutralBackground3Selected} !important`,
    },
    '& > span': {
      backgroundColor: `${tokens.colorNeutralBackground3Selected} !important`,
    },
    '& button': {
      backgroundColor: `${tokens.colorNeutralBackground3Selected} !important`,
    },
    '& svg': {
      color: `${tokens.colorNeutralForeground2Selected} !important`,
    },
  },
  productIcon: {
    padding: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  appIcon: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  docTitle: {
    padding: '14px 12px 14px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  saveStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  statusIcon: {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  searchContainer: {
    width: '360px',
    height: '32px',
  },
  searchInput: {
    width: '100%',
    height: '32px',
    backgroundColor: tokens.colorNeutralBackground2, // Neutral/Background/2/Rest from Figma
    border: 'none',
    borderRadius: '4px !important',
    boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.14), 0px 0px 2px 0px rgba(0,0,0,0.12)',
    paddingLeft: '16px',
    paddingRight: '8px',
    paddingTop: '6px',
    paddingBottom: '6px',
    gap: '12px',
    '& .fui-Input__input': {
      borderRadius: '4px !important',
    },
    '& input': {
      fontSize: '14px',
      color: tokens.colorNeutralForeground3, // Neutral/Foreground/3/Rest from Figma
      lineHeight: '20px',
      fontFamily: '"Segoe UI", sans-serif',
      fontWeight: 'normal',
      '&::placeholder': {
        color: tokens.colorNeutralForeground3, // Neutral/Foreground/3/Rest from Figma
      },
    },
    // Style for the search icon
    '& .fui-Input__contentBefore svg': {
      width: '16px',
      height: '16px',
      color: tokens.colorNeutralForeground2,
    },
  },
  iconButton: {
    padding: '14px',
    minWidth: '48px',
    height: '48px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      '& svg': {
        color: tokens.colorNeutralForeground2Hover,
      },
    },
    '& svg': {
      width: '20px',
      height: '20px',
    },
  },
  searchButton: {
    padding: '14px',
    minWidth: '48px',
    height: '48px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    '& svg': {
      width: '20px',
      height: '20px',
    },
    '@media (max-width: 1024px)': {
      display: 'flex',
    },
  },
  avatar: {
    padding: '8px',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    '& .fui-Avatar': {
      borderRadius: '50%',
    },
  },
  cloudCheckmarkIcon: {
    width: '20px',
    height: '20px',
    color: tokens.colorNeutralForeground1, // #242424
  },
  
  // App Switcher Menu styles - exact Figma specifications
  appSwitcherMenu: {
    backgroundColor: tokens.colorNeutralBackground1, // white
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'flex-start',
    padding: '16px',
    position: 'relative',
    borderRadius: '8px',
    boxShadow: '0px 14px 28px 0px rgba(0,0,0,0.24), 0px 0px 8px 0px rgba(0,0,0,0.2)',
    width: 'fit-content', // Let content determine size
    height: 'fit-content',
  },
  
  // SearchBox container - full width with bottom margin
  menuSearchContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    overflow: 'hidden',
    position: 'relative',
    borderRadius: '4px',
    flexShrink: 0,
    width: '100%',
    marginBottom: '16px',
    '& .fui-SearchBox': {
      paddingLeft: '12px !important',
      paddingRight: '12px !important',
      maxWidth: 'none !important',
    },
    '& .fui-SearchBox input': {
      paddingLeft: '4px !important',
      paddingRight: '0 !important',
      maxWidth: 'none !important',
    },
    '& .fui-SearchBox .fui-Input__contentBefore': {
      marginRight: '0px !important',
    },
  },
  
  // Search box contents styling to match Figma
  searchBoxContents: {
    backgroundColor: tokens.colorNeutralBackground2, // neutral-100
    boxSizing: 'border-box',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    overflow: 'hidden',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: '0px',
    paddingBottom: '0px',
    position: 'relative',
    borderRadius: '4px',
    flexShrink: 0,
    width: '100%',
  },
  
  // App grid - structured layout like Figma
  appGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    alignItems: 'flex-start',
    position: 'relative',
    flexShrink: 0,
    width: '100%',
    height: '217px', // 285px total - 52px for search box and margins (32px + 16px + 4px)
    justifyContent: 'flex-start',
  },

  // App row for structured layout
  appRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '0px',
    alignItems: 'flex-start',
    width: '100%',
  },

  // Divider between rows
  appRowDivider: {
    width: '100%',
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    margin: '16px 0',
    flexShrink: 0,
  },
  
  // App item - exact Figma specifications  
  appItem: {
    backgroundColor: 'rgba(255,255,255,0)', // transparent
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    height: '86px',
    alignItems: 'center',
    maxHeight: '86px',
    maxWidth: '94px',
    minHeight: '86px',
    minWidth: '94px',
    overflow: 'hidden',
    paddingBottom: '8px',
    paddingTop: '12px',
    paddingLeft: '8px',
    paddingRight: '8px',
    position: 'relative',
    borderRadius: '4px',
    flexShrink: 0,
    width: '94px',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },

  // Disabled app item
  appItemDisabled: {
    backgroundColor: 'rgba(255,255,255,0)', // transparent
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    height: '86px',
    alignItems: 'center',
    maxHeight: '86px',
    maxWidth: '94px',
    minHeight: '86px',
    minWidth: '94px',
    overflow: 'hidden',
    paddingBottom: '8px',
    paddingTop: '12px',
    paddingLeft: '8px',
    paddingRight: '8px',
    position: 'relative',
    borderRadius: '4px',
    flexShrink: 0,
    width: '94px',
    border: 'none',
    cursor: 'not-allowed',
    opacity: 0.5,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  
  // App icon - 32px square
  menuAppIcon: {
    position: 'relative',
    flexShrink: 0,
    width: '32px',
    height: '32px',
  },
  
  // App label - exact typography from Figma
  appLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '16px',
    color: tokens.colorNeutralForeground1, // #242424
    textAlign: 'center',
    position: 'relative',
    flexShrink: 0,
    width: 'min-content',
    minWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  
  // Override MenuPopover width
  menuPopoverOverride: {
    '& .fui-MenuPopover': {
      width: '512px !important',
      minWidth: '512px !important',
      maxWidth: '512px !important',
      zIndex: '9999 !important',
    },
  },

  // No results found state
  noResultsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%', // Take full height of the app grid
    width: '100%',
    gap: '12px',
    textAlign: 'center',
  },

  noResultsIcon: {
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  noResultsText: {
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '20px',
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },

  // Settings dropdown menu styles
  settingsMenu: {
    minWidth: '260px',
    width: '260px',
    padding: '4px',
  },

  settingsMenuHeader: {
    padding: '6px 8px',
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    lineHeight: '16px',
    fontFamily: '"Segoe UI", sans-serif',
  },

  settingsMenuItem: {
    minHeight: '32px',
    padding: '0px',
    cursor: 'pointer',
  },

  settingsMenuItemContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px',
    boxSizing: 'border-box',
  },

  settingsMenuText: {
    fontSize: '14px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground2,
    lineHeight: '20px',
    fontFamily: '"Segoe UI", sans-serif',
    flex: 1,
  },

  settingsMenuSwitch: {
    marginLeft: 'auto',
  },

  settingsMenuDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    margin: '4px 0',
  },

  settingsRadioGroup: {
    padding: '4px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  settingsRadioLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    lineHeight: '16px',
    fontFamily: '"Segoe UI", sans-serif',
    marginBottom: '4px',
  },
});

// App-specific icons using local Microsoft 365 product icons
const AppIcon: React.FC<{ appName: OfficeHeaderProps['appName'] }> = ({ appName }) => {
  const iconProps = { width: 24, height: 24, style: { display: 'block' } };
  
  switch (appName) {
    case 'Word':
      return <img src={wordIcon} alt="Word" {...iconProps} />;
    case 'PowerPoint':
      return <img src={powerpointIcon} alt="PowerPoint" {...iconProps} />;
    case 'OneNote':
      return <img src={onenoteIcon} alt="OneNote" {...iconProps} />;
    case 'Excel':
      return <img src={excelIcon} alt="Excel" {...iconProps} />;
    case 'Forms':
      return <img src={formsIcon} alt="Forms" {...iconProps} />;
    default:
      return (
        <svg {...iconProps} viewBox="0 0 20 20" fill="none">
          <path d="M4 2h12c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2z" fill="#0078d4"/>
        </svg>
      );
  }
};

// App switcher icon component (3x3 grid of dots matching Figma design)
const AppSwitcherIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M5.25 4a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Zm12 12a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM16 11.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM17.25 4a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM10 17.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM11.25 10a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM10 5.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM5.25 16a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM4 11.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z" 
      fill="currentColor"
    />
  </svg>
);

const OfficeHeader: React.FC<OfficeHeaderProps> = ({
  appName,
  documentTitle = 'The Global Smart Living Fixtures...',
  // documentName is unused but kept for API compatibility
  documentName: _documentName = 'The Global Smart Living Fixtures...',
  isFixed = false,
  backgroundColor,
  showAppSwitcher = true,
  showSearch = true,
  showSearchBox = true,
  showSettings = true,
  showMore = false,
  showAvatar = true,
  showUndoRedo = false,
  showQuickAccess = false,
  hideAccountButton = false,
  isModified = false,
  customRightSection,
  customLeftSection,
  customAccountMenu,
  customButtons = [],
  quickAccessItems = [],
  onSettingsClick,
  onMoreClick,
  onSave,
  onRename,
  searchPlaceholder = 'Ask Copilot anything',
  avatarName = 'User',
  avatarImage,
  onLLMToggleChange,
}) => {
  // Suppress unused parameter warnings
  void showSearch, showUndoRedo, showQuickAccess, customAccountMenu, quickAccessItems, onSave, onRename, onSettingsClick;
  
  const styles = useStyles();
  const [isAppSwitcherOpen, setIsAppSwitcherOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isMicrosoftLLMEnabled, setIsMicrosoftLLMEnabled] = useState(false);
  const { selectedStyle, setSelectedStyle } = useStyleContext();
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [isLLMAuthenticated, setIsLLMAuthenticated] = useState(false);
  const [isAzureConfigDialogOpen, setIsAzureConfigDialogOpen] = useState(false);
  const [isAzureConfigured, setIsAzureConfigured] = useState(isAzureOpenAIConfigured());

  // Load persisted LLM state on component mount
  useEffect(() => {
    const loadLLMState = () => {
      try {
        const stored = localStorage.getItem('microsoft-llm-state');
        if (stored) {
          const config = JSON.parse(stored);
          setIsMicrosoftLLMEnabled(config.isEnabled || false);
          setIsLLMAuthenticated(config.isAuthenticated || false);
        }
      } catch (error) {
        console.warn('Failed to load LLM state in header:', error);
      }
    };
    
    loadLLMState();
  }, []);

  const handleLLMToggleChange = (checked: boolean) => {
    if (checked && !isLLMAuthenticated) {
      // User wants to enable LLM but is not authenticated - show auth dialog
      setIsAuthDialogOpen(true);
    } else if (!checked && isLLMAuthenticated) {
      // User is disabling LLM - silently clear authentication state without interactive logout
      // We don't call AuthProvider.shared.logOut() to avoid triggering sign-out dialog
      llmService.clearAuthenticationState();
      setIsLLMAuthenticated(false);
      setIsMicrosoftLLMEnabled(false);
      if (onLLMToggleChange) {
        onLLMToggleChange(false, false);
      }
    } else {
      // User is enabling LLM and is already authenticated
      setIsMicrosoftLLMEnabled(checked);
      if (onLLMToggleChange) {
        onLLMToggleChange(checked, isLLMAuthenticated);
      }
    }
  };

  const handleAuthSuccess = () => {
    setIsLLMAuthenticated(true);
    setIsMicrosoftLLMEnabled(true);
    setIsAuthDialogOpen(false);
    if (onLLMToggleChange) {
      onLLMToggleChange(true, true);
    }
  };

  const handleAuthCancel = () => {
    setIsAuthDialogOpen(false);
    // Keep the toggle in its previous state (off)
  };

  const handleAppSelect = (path: string, disabled?: boolean) => {
    if (disabled) return; // Don't navigate if disabled
    setIsAppSwitcherOpen(false);
    window.location.href = path;
  };

  const headerClasses = `${styles.header} ${isFixed ? styles.headerFixed : ''}`;
  const headerStyle = backgroundColor ? { backgroundColor } : {};

  return (
    <header className={headerClasses} style={headerStyle} role="banner">
      {/* Left Section */}
      <div className={styles.leftSection}>
        {customLeftSection || (
          <>
            {showAppSwitcher && (
              <Menu 
                open={isAppSwitcherOpen} 
                onOpenChange={(_, data) => setIsAppSwitcherOpen(data.open)}
                positioning={{ position: "below", align: "start", offset: { crossAxis: 0, mainAxis: 4 } }}
              >
                <MenuTrigger>
                  <Button
                    appearance="subtle"
                    size="medium"
                    shape="square"
                    className={styles.appSwitcher}
                    style={isAppSwitcherOpen ? {
                      backgroundColor: tokens.colorNeutralBackground3Selected,
                    } : {}}
                    title="App switcher"
                    aria-label="App switcher"
                    aria-pressed={isAppSwitcherOpen}
                    icon={<AppSwitcherIcon />}
                  />
                </MenuTrigger>
                <MenuPopover 
                  className={styles.menuPopoverOverride}
                  style={{
                    width: '512px',
                    minWidth: '512px',
                    maxWidth: '512px',
                    height: '285px',
                    padding: '16px',
                    backgroundColor: tokens.colorNeutralBackground1,
                    borderRadius: '8px',
                    boxShadow: '0px 14px 28px 0px rgba(0,0,0,0.24), 0px 0px 8px 0px rgba(0,0,0,0.2)',
                    border: 'none',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}
                >
                  <AppSwitcherMenu onAppSelect={handleAppSelect} />
                </MenuPopover>
              </Menu>
            )}
            
            <div className={styles.productIcon}>
              <div className={styles.appIcon}>
                <AppIcon appName={appName} />
              </div>
            </div>

            <div className={styles.docTitle}>
              <Text
                size={300}
                weight="regular"
              >
                {documentTitle || 'The Global Smart Living Fixtures...'}
                {isModified && ' *'}
              </Text>
              <div className={styles.saveStatus}>
                {appName !== 'OneNote' && (
                  <div className={styles.statusIcon}>
                    <img src="/src/assets/office/ribbon/Star.svg" alt="Star" className={styles.cloudCheckmarkIcon} />
                  </div>
                )}
                <div className={styles.statusIcon}>
                  <img src="/src/assets/office/ribbon/MipTagLockDynamicFillWithOutline.svg" alt="Tag lock" className={styles.cloudCheckmarkIcon} />
                </div>
                {appName !== 'OneNote' && (
                  <div className={styles.statusIcon}>
                    <img src="/src/assets/office/ribbon/CloudFont.svg" alt="Saved to cloud" className={styles.cloudCheckmarkIcon} />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Center Section - Search */}
      {showSearchBox && (
        <div className={styles.centerSection}>
          <div className={styles.searchContainer}>
            <Input
              className={styles.searchInput}
              contentBefore={<img src="/src/assets/office/ribbon/Search Sparkle.svg" alt="Search" style={{ width: '16px', height: '16px' }} />}
              placeholder={searchPlaceholder}
              size="medium"
              appearance="filled-lighter"
              aria-label="Search"
              role="searchbox"
            />
          </div>
        </div>
      )}

      {/* Right Section */}
      <div className={styles.rightSection}>
        {customRightSection || (
          <>
            {customButtons.map((button, index) => (
              <Button
                key={index}
                appearance={button.variant === 'primary' ? 'primary' : 'subtle'}
                size="medium"
                shape="square"
                className={styles.iconButton}
                onClick={button.onClick}
              >
                {button.text}
              </Button>
            ))}

            {/* Responsive Search Button - shows at 1024px and below */}
            {showSearchBox && (
              <Button
                appearance="subtle"
                size="medium"
                shape="square"
                className={styles.searchButton}
                onClick={() => {
                  // Add search functionality here
                  console.log('Search clicked');
                }}
                title="Search"
                aria-label="Search"
                icon={<SearchRegular />}
              />
            )}

            {showSettings && (
              <Menu 
                open={isSettingsMenuOpen} 
                onOpenChange={(_, data) => setIsSettingsMenuOpen(data.open)}
                positioning={{ position: "below", align: "end", offset: { crossAxis: 0, mainAxis: 4 } }}
              >
                <MenuTrigger>
                  <Button
                    appearance="subtle"
                    size="medium"
                    shape="square"
                    className={styles.iconButton}
                    style={isSettingsMenuOpen ? {
                      backgroundColor: tokens.colorNeutralBackground3Selected,
                    } : {}}
                    title="Settings"
                    aria-label="Settings"
                    aria-pressed={isSettingsMenuOpen}
                    icon={<SettingsRegular />}
                  />
                </MenuTrigger>
                <MenuPopover className={styles.settingsMenu}>
                  <MenuList>
                    <div className={styles.settingsMenuHeader}>
                      Settings
                    </div>
                    <div 
                      className={styles.settingsMenuItem}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLLMToggleChange(!isMicrosoftLLMEnabled);
                      }}
                      role="menuitem"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLLMToggleChange(!isMicrosoftLLMEnabled);
                        }
                      }}
                    >
                      <div className={styles.settingsMenuItemContent}>
                        <span className={styles.settingsMenuText}>Microsoft LLM</span>
                        <Switch 
                          className={styles.settingsMenuSwitch}
                          checked={isMicrosoftLLMEnabled}
                          onChange={(_, data) => {
                            // Don't stop propagation here - let the Switch handle its own click
                            handleLLMToggleChange(data.checked);
                          }}
                          onClick={(e) => {
                            // Stop propagation only for the Switch to prevent the div onClick from firing
                            e.stopPropagation();
                          }}
                        />
                      </div>
                    </div>
                    <div className={styles.settingsMenuDivider} />
                    <div className={styles.settingsRadioGroup}>
                      <span className={styles.settingsRadioLabel}>Display Style</span>
                      <RadioGroup
                        value={selectedStyle}
                        onChange={(_, data) => setSelectedStyle(data.value as StyleType)}
                      >
                        <Radio value="styleC" label="Summary + Copilot Button" />
                        <Radio value="styleD" label="Pills + Copilot Button" />
                        <Radio value="styleA" label="Summary + Pills + Input" />
                        <Radio value="styleB" label="Summary + Input" />
                      </RadioGroup>
                    </div>
                    <div className={styles.settingsMenuDivider} />
                    <div 
                      className={styles.settingsMenuItem}
                      onClick={() => {
                        setIsAzureConfigDialogOpen(true);
                        setIsSettingsMenuOpen(false);
                      }}
                      role="menuitem"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsAzureConfigDialogOpen(true);
                          setIsSettingsMenuOpen(false);
                        }
                      }}
                    >
                      <div className={styles.settingsMenuItemContent}>
                        <span className={styles.settingsMenuText}>
                          Configure Azure OpenAI {isAzureConfigured ? '✓' : ''}
                        </span>
                      </div>
                    </div>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}

            {showMore && (
              <Button
                appearance="subtle"
                size="medium"
                shape="square"
                className={styles.iconButton}
                onClick={onMoreClick}
                title="More"
                aria-label="More options"
                icon={<MoreHorizontalRegular />}
              />
            )}

            {showAvatar && !hideAccountButton && (
              <div className={styles.avatar}>
                <Avatar
                  name={avatarName}
                  image={avatarImage ? { src: avatarImage } : undefined}
                  size={32}
                />
              </div>
            )}
          </>
        )}
      </div>
      
      {/* LLM Authentication Dialog */}
      <LLMAuthDialog
        isOpen={isAuthDialogOpen}
        onClose={() => setIsAuthDialogOpen(false)}
        onAuthSuccess={handleAuthSuccess}
        onAuthCancel={handleAuthCancel}
      />
      
      {/* Azure OpenAI Configuration Dialog */}
      <AzureOpenAIConfigDialog
        isOpen={isAzureConfigDialogOpen}
        onClose={() => setIsAzureConfigDialogOpen(false)}
        onSave={() => {
          setIsAzureConfigured(true);
          setIsAzureConfigDialogOpen(false);
        }}
      />
    </header>
  );
};

// No Results Found Icon - using the search exclamation mark icon
const NoResultsIcon: React.FC = () => (
  <div style={{ 
    width: '64px', 
    height: '64px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center'
  }}>
    <img 
      src="/src/assets/shared/Images/Search_Exclamationmark.svg" 
      alt="No results found" 
      style={{ 
        width: '64px', 
        height: '64px',
        display: 'block'
      }} 
    />
  </div>
);

// App data for the switcher menu
const apps = [
  {
    name: 'Word',
    icon: wordIcon,
    path: '/word-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'PowerPoint',
    icon: powerpointIcon,
    path: '/powerpoint-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'Excel',
    icon: excelIcon,
    path: '/excel-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'OneNote',
    icon: onenoteIcon,
    path: '/onenote-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'Forms',
    icon: formsIcon,
    path: '/forms-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'Home',
    icon: HomeRegular,
    path: '/home',
    iconType: 'fluent' as const,
    disabled: false,
  },
  {
    name: 'Custom Canvas',
    icon: CodeRegular,
    path: '/blank-silhouette',
    iconType: 'fluent' as const,
    disabled: false,
  },
];

// App Switcher Menu component - no outer container, use MenuPopover as container
const AppSwitcherMenu: React.FC<{ onAppSelect: (path: string, disabled?: boolean) => void }> = ({ onAppSelect }) => {
  const styles = useStyles();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredApps = apps.filter(app => 
    app.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* SearchBox matching Figma design */}
      <div className={styles.menuSearchContainer}>
        <SearchBox
          placeholder="Find Microsoft 365 apps"
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value || '')}
          size="medium"
          appearance="filled-lighter"
          style={{
            width: '100%',
            backgroundColor: tokens.colorNeutralBackground2,
            border: 'none',
            borderRadius: '4px',
          }}
        />
      </div>
      
      {/* App List - structured layout like Figma */}
      <div className={styles.appGrid}>
        {filteredApps.length > 0 ? (
          <>
            {/* Top row: Other apps (Home, Custom Canvas) */}
            <div className={styles.appRow}>
              {filteredApps
                .filter(app => ['Home', 'Custom Canvas'].includes(app.name))
                .map((app) => (
                  <button
                    key={app.name}
                    className={app.disabled ? styles.appItemDisabled : styles.appItem}
                    onClick={() => onAppSelect(app.path, app.disabled)}
                    disabled={app.disabled}
                  >
                    <div className={styles.menuAppIcon}>
                      {app.iconType === 'image' ? (
                        <img 
                          src={app.icon as string} 
                          alt={app.name}
                          style={{ width: '32px', height: '32px' }}
                        />
                      ) : (
                        React.createElement(app.icon as React.ComponentType<React.SVGProps<SVGSVGElement>>, { 
                          style: { width: '32px', height: '32px' } 
                        })
                      )}
                    </div>
                    <Text className={styles.appLabel}>
                      {app.name}
                    </Text>
                  </button>
                ))}
            </div>
            
            {/* Divider between rows - only show when no search */}
            {!searchQuery && <div className={styles.appRowDivider}></div>}
            
            {/* Bottom row: Office apps (Word, PowerPoint, Excel, OneNote, Forms) */}
            <div className={styles.appRow}>
              {filteredApps
                .filter(app => ['Word', 'PowerPoint', 'Excel', 'OneNote', 'Forms'].includes(app.name))
                .map((app) => (
                  <button
                    key={app.name}
                    className={app.disabled ? styles.appItemDisabled : styles.appItem}
                    onClick={() => onAppSelect(app.path, app.disabled)}
                    disabled={app.disabled}
                  >
                    <div className={styles.menuAppIcon}>
                      {app.iconType === 'image' ? (
                        <img 
                          src={app.icon as string} 
                          alt={app.name}
                          style={{ width: '32px', height: '32px' }}
                        />
                      ) : (
                        React.createElement(app.icon as React.ComponentType<React.SVGProps<SVGSVGElement>>, { 
                          style: { width: '32px', height: '32px' } 
                        })
                      )}
                    </div>
                    <Text className={styles.appLabel}>
                      {app.name}
                    </Text>
                  </button>
                ))}
            </div>
          </>
        ) : (
          /* No results found state */
          <div className={styles.noResultsContainer}>
            <div className={styles.noResultsIcon}>
              <NoResultsIcon />
            </div>
            <Text className={styles.noResultsText}>
              No results found
            </Text>
          </div>
        )}
      </div>
    </>
  );
};

export default OfficeHeader;
